<template>
  <div class="tools-double-verification">
    <!-- 页面标题 -->
    <div class="page-header">
      <h1 class="page-title">双重安全验证</h1>
      <p class="page-description">管理域名安全验证配置，支持跳过验证和链接复制</p>
    </div>

    <!-- 搜索区域 - 居中显示，参考百度搜索样式 -->
    <div class="search-section">
      <div class="search-container">
        <el-input
          v-model="searchDomain"
          placeholder="搜索域名..."
          clearable
          size="large"
          class="baidu-style-search"
          @input="handleSearch"
        >
          <template #prefix>
            <el-icon class="search-icon">
              <Search />
            </el-icon>
          </template>
        </el-input>
      </div>
    </div>

    <!-- 数据表格 -->
    <div class="table-section">
      <el-table :data="tableData" v-loading="loading" empty-text="暂无数据" class="verification-table">
        <!-- 序号列 -->
        <el-table-column label="序号" width="80" align="center">
          <template #default="scope">
            <span class="index-number">{{ scope.$index + 1 }}</span>
          </template>
        </el-table-column>

        <!-- 域名列 -->
        <el-table-column label="域名" min-width="200">
          <template #default="scope">
            <div class="domain-cell">
              <span class="domain-text" @click="copyDomain(scope.row.domain)" @dblclick="jumpToDomain(scope.row.domain)" :title="`单击复制，双击跳转到 ${scope.row.domain}`">
                {{ scope.row.domain }}
              </span>
              <el-tooltip content="单击复制，双击跳转" placement="top">
                <el-icon class="domain-hint">
                  <InfoFilled />
                </el-icon>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>

        <!-- 过期时间列 -->
        <el-table-column label="跳过验证过期时间" width="300" align="center">
          <template #default="scope">
            <span :class="{
                'expired-time': isExpired(scope.row.access_key_expire_time),
                'valid-time': !isExpired(scope.row.access_key_expire_time)
              }">
              {{ formatTime(scope.row.access_key_expire_time) }}
            </span>
            <el-tag v-if="isExpired(scope.row.access_key_expire_time)" type="danger" size="small" class="expired-tag">
              已过期
            </el-tag>
          </template>
        </el-table-column>

        <!-- 操作列 -->
        <el-table-column label="操作" width="300" align="center" fixed="right">
          <template #default="scope">
            <div class="action-buttons">
              <el-button type="primary" size="small" @click="handleSkipVerification(scope.row)" class="skip-btn">
                <el-icon>
                  <Link />
                </el-icon>
                跳过验证
              </el-button>
              <el-button type="success" size="small" @click="handleCopyLink(scope.row)" class="copy-btn">
                <el-icon>
                  <DocumentCopy />
                </el-icon>
                复制跳过链接
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 空状态 -->
    <div v-if="tableData.length === 0 && !loading" class="empty-state">
      <el-empty description="暂无验证配置数据" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  Search,
  Link,
  DocumentCopy,
  InfoFilled
} from '@element-plus/icons-vue'
import { getDoubleVerificationList } from '@/utils/restApi'

// 响应式数据
const tableData = ref([])
const loading = ref(false)
const searchDomain = ref('')

// 加载数据
const loadData = async () => {
  loading.value = true
  try {
    const response = await getDoubleVerificationList(searchDomain.value)
    tableData.value = Array.isArray(response) ? response : []
  } catch (error) {
    console.error('加载双重验证列表失败:', error)
    ElMessage.error('加载数据失败，请重试')
    tableData.value = []
  } finally {
    loading.value = false
  }
}

// 处理搜索
const handleSearch = () => {
  loadData()
}

// 复制域名
const copyDomain = async (domain) => {
  try {
    await navigator.clipboard.writeText(domain)
    ElMessage.success('复制成功')
  } catch (error) {
    console.error('复制失败:', error)
    ElMessage.error('复制失败，请手动复制')
  }
}

// 跳转到域名
const jumpToDomain = (domain) => {
  if (domain && domain.startsWith('http')) {
    window.open(domain, '_blank')
  } else {
    window.open(`https://${domain}`, '_blank')
  }
}

// 检查是否过期
const isExpired = (expireTime) => {
  if (!expireTime) return true
  const expireDate = new Date(expireTime)
  const now = new Date()
  return expireDate < now
}

// 格式化时间
const formatTime = (time) => {
  if (!time) return '-'
  return time.replace(' ', '\n')
}

// 处理跳过验证
const handleSkipVerification = (row) => {
  // if (isExpired(row.access_key_expire_time)) {
  //   ElMessage.warning('该链接已过期，无法跳过验证')
  //   return
  // }

  const skipUrl = `${row.call_back}?token=${row.access_key}&transfer=redirect`
  window.open(skipUrl, '_blank')
}

// 处理复制链接
const handleCopyLink = async (row) => {
  const skipUrl = `${row.call_back}?token=${row.access_key}&transfer=redirect`

  try {
    await navigator.clipboard.writeText(skipUrl)
    ElMessage.success('复制成功')
  } catch (error) {
    console.error('复制失败:', error)
    // 降级方案：使用 textarea 复制
    const textarea = document.createElement('textarea')
    textarea.value = skipUrl
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
    ElMessage.success('复制成功')
  }
}

// 页面加载时获取数据
onMounted(() => {
  loadData()
})
</script>

<style scoped>
.tools-double-verification {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  min-height: calc(100vh - 120px);
}

/* 页面标题样式 */
.page-header {
  text-align: center;
  margin-bottom: 32px;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.page-description {
  font-size: 1.1rem;
  color: #6b7280;
  margin: 0;
}

/* 搜索区域样式 - 百度风格 */
.search-section {
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
}

.search-container {
  width: 100%;
  max-width: 600px;
}

.baidu-style-search {
  width: 100%;
}

.baidu-style-search :deep(.el-input__wrapper) {
  border-radius: 24px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border: 1px solid #e8e8e8;
  transition: all 0.3s ease;
}

.baidu-style-search :deep(.el-input__wrapper:hover) {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  border-color: #409eff;
}

.baidu-style-search :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 4px 20px rgba(64, 158, 255, 0.2);
  border-color: #409eff;
}

.search-icon {
  color: #909399;
  font-size: 18px;
}

/* 表格区域样式 */
.table-section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.verification-table {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
}

.verification-table :deep(.el-table__header) {
  background: #f5f7fa;
}

.verification-table :deep(.el-table__row:hover > td) {
  background-color: #f5f7fa !important;
}

/* 序号样式 */
.index-number {
  font-weight: 600;
  color: #667eea;
  font-size: 14px;
}

/* 域名单元格样式 */
.domain-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.domain-text {
  cursor: pointer;
  color: #1890ff;
  text-decoration: none;
  transition: color 0.3s ease;
  font-weight: 500;
}

.domain-text:hover {
  color: #40a9ff;
  text-decoration: underline;
}

.domain-hint {
  color: #bfbfbf;
  font-size: 12px;
}

/* 时间样式 */
.expired-time {
  color: #ff4d4f;
  font-weight: 500;
}

.valid-time {
  color: #52c41a;
  font-weight: 500;
}

.expired-tag {
  margin-left: 8px;
}

/* 操作按钮样式 */
.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.skip-btn,
.copy-btn {
  display: flex;
  align-items: center;
  gap: 4px;
}

.skip-btn:deep(.el-icon),
.copy-btn:deep(.el-icon) {
  font-size: 14px;
}

/* 空状态样式 */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .tools-double-verification {
    padding: 16px;
  }

  .page-title {
    font-size: 2rem;
  }

  .search-container {
    max-width: 100%;
  }

  .action-buttons {
    flex-direction: column;
    gap: 4px;
  }

  .skip-btn,
  .copy-btn {
    width: 100%;
  }
}
</style>
